import RecordPages from '@/components/RecordPages';
import SubjectMediaFields from '@/components/SubjectMediaFields';
import BilingualField from '@/components/BilingualField';
import { descriptionVersions } from "@/lib/profileLanguages";
import MainImagePicker from "@/components/MainImagePicker";
import { hasPlantManagement, hasPlantAreas, hasCropCultivation } from "@/lib/subjects";
import SubjectLocationFields from "@/components/SubjectLocationFields";
import ReferenceCatalog from "@/components/ReferenceCatalog";
import PlantHeritage from "@/components/PlantHeritage";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PaymentOfferFields from "@/components/PaymentOfferFields";
import ProjectFields from "@/components/ProjectFields";
import OpportunityMediaFields from "@/components/OpportunityMediaFields";
import LivestockUnits from "@/components/LivestockUnits";
import PracticesFields from "@/components/PracticesFields";
import CropAreas, { serializeAreas } from "@/components/CropAreas";
import GalleryEditor from "@/components/GalleryEditor";
import SubjectFields from "@/components/SubjectFields";

const OPP_TYPES = ["BUY", "ADOPT", "VISIT", "LEARN", "VOLUNTEER", "SUPPORT", "PROJECT"];
const csv = (a) => (Array.isArray(a) ? a.join(", ") : "");
const fromCsv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const opportunityVersions = (value, originalLanguage = "it") => typeof value === "object" && value ? value : { [originalLanguage]: value || "" };
const num = (v) => (v === "" || v == null ? null : Number(v));

const Fld = ({ label, children }) => (
  <div>
    <Label className="text-sm text-[#4A443D] mb-1.5 block">{label}</Label>
    {children}
  </div>
);
const inputCls = "bg-white border-[#E2DACB]";

export default function FarmEdit() {
  const { id } = useParams();
  const { t, loc, lang } = useLang();
  const navigate = useNavigate();
  const [farm, setFarm] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mediaUploading,setMediaUploading] = useState(false);
  const [data, setData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [dirty, setDirty] = useState(new Set());
  const it = lang === "it";
  const [delPreview, setDelPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editPage, setEditPage] = useState("identity");
  const [saveSeconds, setSaveSeconds] = useState(0);
  useEffect(() => {
    if (!busy) { setSaveSeconds(0); return; }
    const started = Date.now();
    const timer = setInterval(() => setSaveSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  const openDelete = async () => {
    try {
      const { data: p } = await api.get(`/farms/${id}/deletion-preview`);
      setDelPreview(p);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/farms/${id}`);
      toast.success(it ? "Profilo archiviato (recuperabile). Un amministratore può ripristinarlo." : "Profile archived (recoverable). An administrator can restore it.");
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDeleting(false); }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/farms/${id}/subscription`).then(r => setSubscription(r.data)).catch(() => {});
    api.get(`/farms/${id}`).then((r) => {
      const f = r.data;
      setFarm(f);
      setData({
        references: { reference_crop_ids: f.reference_crop_ids || [], reference_botanical_selections: f.reference_botanical_selections || [] },
        subject: { subject_type: f.subject_type || "farm", activities: f.activities || ["agriculture"], subject_details: f.subject_details || {}, mushroom_units: f.mushroom_units, original_language: f.original_language || "it", subject_translations: f.subject_translations || {}, portfolio_files: f.portfolio_files || [], linked_farm_ids: f.linked_farm_ids || [] },
        livestock_units: f.livestock_units || [],
        plant_heritage: f.plant_heritage || [],
        general: {
          name: typeof f.name === "string" ? f.name : loc(f.name),
          desc_en: descriptionVersions(f).en || "",
          desc_it: descriptionVersions(f).it || "",
          locality: f.location?.locality || "",
          municipality: f.location?.municipality || "",
          region: f.location?.region || "",
          country: f.location?.country || "",
          lat: f.location?.lat ?? null, lng: f.location?.lng ?? null,
          approximate: f.location?.approximate ?? true, manual_coordinates: false,
          image: f.cover_image || "",
          profile_image: f.profile_image || "",
          website: f.contact?.website || "",
          email: f.contact?.email || "",
          shop_url: f.contact?.shop_url || "",
          social_links: f.contact?.social_links || {},
          video: f.video || null,
        },
        fields: (f.fields || []).map((x) => ({
          ...x, id: x.id, name: typeof x.name === "string" ? x.name : loc(x.name),
          area_ha: x.area_ha ?? "", area_value: x.area_value ?? (x.area_ha ?? ""), area_unit: x.area_unit || "ha", crop: x.crop || "", trees: x.trees ?? "",
          water_methods: csv(x.water_methods), water_source: x.water_source || "",
        })),
        crops: (f.crops || []).map((x) => ({
          name: x.name || "", scientific_name: x.scientific_name || "", varieties: csv(x.varieties),
          image: x.image || "",
        })),
        practices: {
          text_translations: f.practices?.text_translations || {},
          water_methods: csv(f.practices?.water_methods), water_source: f.practices?.water_source || "",
          soil: csv(f.practices?.soil), biodiversity: csv(f.practices?.biodiversity), agricultural: csv(f.practices?.agricultural),
        },
        brands: (f.brands || []).map((b) => ({
          id: b.id, name: b.name || "", shop_url: b.shop_url || "",
          products: (b.products || []).map((p) => ({
            id: p.id, name: opportunityVersions(p.name, f.original_language || "it"),
            description: opportunityVersions(p.description, f.original_language || "it"),
            external_url: p.external_url || "", image: p.image || "",
          })),
        })),
        opportunities: (f.opportunities || []).map((o) => ({
          ...o, id: o.id, type: o.type, title: opportunityVersions(o.title, f.original_language || "it"),
          description: opportunityVersions(o.description, f.original_language || "it"),
          external_url: o.external_url || "", season: o.season || "",
        })),
      });
    }).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    // eslint-disable-next-line
  }, [id]);

  const upd = useCallback((section, value) => {
    setData((d) => ({ ...d, [section]: value }));
    setDirty((s) => new Set(s).add(section));
  }, []);

  const localizedEdit = (original, displayed) => {
    if (!original || typeof original === "string") return displayed;
    return loc(original) === displayed ? original : { ...original, [lang]: displayed };
  };

  const save = async () => {
    setBusy(true); setErr("");
    const payload = {};
    if (dirty.has("plant_heritage")) payload.plant_heritage = data.plant_heritage;
    if (dirty.has("references")) Object.assign(payload, data.references);
    if (dirty.has("subject")) Object.assign(payload, data.subject);
    if (dirty.has("livestock_units")) payload.livestock_units = data.livestock_units;
    if (dirty.has("general")) {
      const g = data.general;
      payload.name = g.name;
      payload.description = { en: g.desc_en, it: g.desc_it };
      payload.location = {
        ...(farm.location || {}),
        locality: g.locality || null, municipality: g.municipality || null,
        region: g.region || null, country: g.country || null,
        lat: g.lat ?? null, lng: g.lng ?? null, approximate: g.approximate ?? true, manual_coordinates: !!g.manual_coordinates,
      };
      payload.cover_image = g.image || null;
      payload.profile_image = g.profile_image || null;
      payload.contact = { website: g.website || null, email: g.email || null, shop_url: g.shop_url || null, social_links: g.social_links || {} };
      payload.video = g.video || null;
    }
    if (dirty.has("fields")) {
      payload.fields = serializeAreas(data.fields).map((x) => ({
        ...(farm.fields || []).find((old) => old.id === x.id),
        ...x, id: x.id || crypto.randomUUID(), name: localizedEdit((farm.fields || []).find((old) => old.id === x.id)?.name, x.name), area_ha: num(x.area_ha), crop: x.crop || null,
        trees: num(x.trees), water_methods: x.water_methods, water_source: x.water_source || null,
      }));
    }
    if (dirty.has("crops")) {
      payload.crops = data.crops.map((x) => ({
        ...(farm.crops || []).find((old) => old.name === x.name),
        name: x.name, scientific_name: x.scientific_name || null, varieties: fromCsv(x.varieties),
        image: x.image || null,
      }));
      if (data.crops[0]) payload.main_crop = data.crops[0].name;
    }
    if (dirty.has("practices") && hasPlantManagement(data.subject.activities)) {
      payload.practices = {
        ...(farm.practices || {}),
        text_translations: data.practices.text_translations || {},
        water_methods: fromCsv(data.practices.water_methods), water_source: data.practices.water_source || null,
        soil: fromCsv(data.practices.soil), biodiversity: fromCsv(data.practices.biodiversity), agricultural: fromCsv(data.practices.agricultural),
      };
    }
    if (dirty.has("brands")) {
      payload.brands = data.brands.map((b) => ({
        ...(farm.brands || []).find((old) => old.id === b.id),
        id: b.id || crypto.randomUUID(), name: b.name, shop_url: b.shop_url || null,
        products: b.products.map((p) => ({
          ...(farm.brands || []).find((old) => old.id === b.id)?.products?.find((old) => old.id === p.id),
          id: p.id || crypto.randomUUID(),
          name: opportunityVersions(p.name, farm.original_language || "it"),
          description: opportunityVersions(p.description, farm.original_language || "it"),
          external_url: p.external_url || null, image: p.image || null,
        })),
      }));
    }
    if (dirty.has("opportunities")) {
      payload.opportunities = data.opportunities.map((o) => lockedOpportunity(o) ? (farm.opportunities || []).find(old => old.id === o.id) : ({
        ...(farm.opportunities || []).find((old) => old.id === o.id),
        id: o.id || crypto.randomUUID(), type: o.type,
        title: opportunityVersions(o.title, farm.original_language || "it"),
        description: opportunityVersions(o.description, farm.original_language || "it"),
        external_url: o.external_url || null, external_urls: o.external_urls || {}, season: o.season || null,
        ...(o.type === "PROJECT" ? { project_details: { ...(o.project_details || {}), partners: (o.project_details?.partners || []).map(p => p.trim()).filter(Boolean), related_opportunity_ids: (o.project_details?.related_opportunity_ids || []).filter(id => data.opportunities.some(item => item.id === id && item.type !== "PROJECT")) }, start_date: o.start_date || null, end_date: o.end_date || null } : {}),
        payment_offer: o.payment_offer || null,
        ...(o.type !== "PROJECT" ? { media: o.media || null } : {}),
        active: (farm.opportunities || []).find((old) => old.id === o.id)?.active ?? true,
      }));
    }
    try {
      await api.patch(`/farms/${id}`, payload);
      toast.success(t("auth.saved"));
      setDirty(new Set());
      navigate("/dashboard");
    } catch (e) {
      const m = formatApiError(e.response?.data?.detail) || t("auth.saveError");
      setErr(m); toast.error(m);
    } finally { setBusy(false); }
  };

  if (err && !data) return <div className="grain-bg min-h-[60vh] flex items-center justify-center font-sans text-[#B3261E]">{err}</div>;
  if (!data) return <div className="h-[60vh] grain-bg flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 border-[#E2DACB] border-t-[#557448] animate-spin" /></div>;

  const hasPro = ['pro', 'founder_pro'].includes(subscription?.effective_plan);
  const lockedOpportunity = o => !hasPro && !(data.subject.subject_type === 'public_body' && o.type === 'PROJECT');
  const canAddOpportunity = hasPro ? data.opportunities.length < (subscription?.entitlements?.max_opportunities ?? 100) : data.subject.subject_type === 'public_body' && data.opportunities.length === 0;
  const projectLimit = ['pro', 'founder_pro'].includes(subscription?.effective_plan) ? subscription.entitlements?.max_projects ?? 100 : data.subject.subject_type === 'public_body' ? 1 : 0;
  const g = data.general;
  const setG = (k, v) => upd("general", { ...g, [k]: v });

  const pages = [
    ["identity", it ? "Identità e attività" : "Identity and activities"],
    ["general", it ? "Informazioni e contatti" : "Information and contacts"],
    ["heritage", it ? "Colture e patrimonio" : "Crops and heritage"],
    ...(data.subject.activities.includes("mushrooms") ? [["fungi",it?"Fungaia":"Mushroom farm"]] : []),
    ...(hasPlantAreas(data.subject.activities) ? [["areas", it ? "Aree e pratiche" : "Areas and practices"]] : []),
    ["gallery", it ? "Foto, social e video" : "Photos, social and video"],
    ["products", it ? "Marchi e prodotti" : "Brands and products"],
    ["opportunities", it ? "Progetti e opportunità" : "Projects and opportunities"],
  ];
  const pageIndex = Math.max(0, pages.findIndex(([key]) => key === editPage));
  const activePage = pages[pageIndex][0];
  const changePage = key => { setEditPage(key); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <div className="grain-bg min-h-[calc(100vh-72px)]" data-testid="farm-edit-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#6E6258] hover:text-[#557448] mb-4">
          <ArrowLeft size={15} /> {t("auth.backToDash")}
        </Link>
        <h1 className="font-serif text-3xl font-semibold text-[#2A2521] mb-1">{farm.name}</h1>
        <p className="font-sans text-[#6E6258] mb-8">{t("auth.editFarm")}</p>

        <nav aria-label={it ? "Sezioni della modifica profilo" : "Profile editing sections"} className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {pages.map(([key, label], index) => <button key={key} type="button" aria-current={activePage === key ? "step" : undefined} onClick={() => changePage(key)} className={`rounded-lg border px-3 py-3 text-left text-sm ${activePage === key ? "bg-[#557448] text-white border-[#557448]" : "bg-[#FFFDF8] border-[#E2DACB] text-[#4A443D]"}`}>{index + 1}. {label}</button>)}
        </nav>
        <p className="text-sm text-[#6E6258] mb-5">{it ? "Passa da una sezione all’altra senza perdere le modifiche. Al termine premi Salva. Le foto della galleria si salvano separatamente al caricamento." : "Switch sections without losing your changes. Press Save when finished. Gallery photos are saved separately when uploaded."}</p>
        <div hidden={activePage !== "identity"}>
        <Section title={lang === "it" ? "Soggetto e attività" : "Subject and activities"}>
          <SubjectFields value={data.subject} onChange={(value) => upd("subject", value)} />
        </Section>
        {data.subject.activities.includes("livestock") && <Section title={lang === "it" ? "Allevamento" : "Livestock"}>
          <LivestockUnits originalLanguage={data.subject.original_language} value={data.livestock_units} onChange={value => upd("livestock_units", value)} />
          {!!data.subject.subject_details?.species && <details className="text-sm mt-4"><summary>{lang === "it" ? "Descrizioni precedenti conservate" : "Preserved previous descriptions"}</summary><p>{data.subject.subject_details.species}</p><p>{data.subject.subject_details.breeds}</p></details>}
        </Section>}
        </div>
        {data.subject.activities.includes('mushrooms') && <div hidden={activePage !== 'fungi'}><Section title={it?'Fungaia e funghi coltivati':'Mushroom farm and cultivated fungi'}><SubjectFields value={data.subject} onChange={value=>upd('subject',value)} mode="fungi"/></Section></div>}
        <div hidden={activePage !== "heritage"}>
        <Section title={lang === "it" ? "Colture e specie di riferimento" : "Reference crops and species"}>
          <ReferenceCatalog activities={data.subject.activities} areas={data.fields} onAreasChange={rows => upd("fields", rows)} value={data.references} onChange={v => upd("references", v)} />
        </Section>
        <details open={data.plant_heritage.length>0}><summary className="cursor-pointer font-serif text-xl mb-3">{it?'Censimenti e interventi sul patrimonio vegetale (facoltativi)':'Plant heritage surveys and interventions (optional)'}</summary><Section title={lang === 'it' ? 'Piante e alberature' : 'Plants and trees'}><PlantHeritage originalLanguage={data.subject.original_language} value={data.plant_heritage} onChange={v => upd('plant_heritage', v)} subjectType={data.subject.subject_type} /></Section></details>
        </div>
        <div hidden={activePage !== "general"}>
        {/* General */}
        <Section title={t("auth.secGeneral")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Fld label={t("auth.farmName")}><Input data-testid="edit-name" value={g.name} onChange={(e) => setG("name", e.target.value)} className={inputCls} /></Fld>
            <Fld label={lang === "it" ? "Foto o logo del profilo" : "Profile photo or logo"}><MainImagePicker value={g.profile_image} onChange={value => setG("profile_image", value)} lang={lang} variant="profile" /></Fld>
            <Fld label={lang === "it" ? "Immagine di copertina" : "Cover image"}><MainImagePicker value={g.image} onChange={value => setG("image", value)} lang={lang} /></Fld>
            <div className="sm:col-span-2"><SubjectLocationFields value={g} onChange={v => upd("general", v)} prefix="edit" /></div>
            <Fld label={t("auth.website")}><Input value={g.website} onChange={(e) => setG("website", e.target.value)} className={inputCls} /></Fld>
            <Fld label={t("auth.contactEmail")}><Input value={g.email} onChange={(e) => setG("email", e.target.value)} className={inputCls} /></Fld>
          </div>
          {g.shop_url && (
            <div className="mt-3 rounded-md border border-[#E3B24C] bg-[#FBF4DE] text-[#5C4636] px-4 py-3 text-sm">
              {it
                ? `Hai un vecchio link shop generale (${g.shop_url}). Il link allo shop ora va associato a una singola produzione, qui sotto in \u201cProdotti e produzioni\u201d. L'URL precedente resta conservato finch\u00e9 non lo sposti.`
                : `You have a legacy general shop link (${g.shop_url}). The shop link now belongs to a single production, below in \u201cProducts and productions\u201d. The previous URL is kept until you move it.`}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Fld label={lang === "it" ? "Descrizione — English (facoltativa)" : "Description — English"}><Textarea rows={4} value={g.desc_en} onChange={(e) => setG("desc_en", e.target.value)} className={inputCls} /></Fld>
            <Fld label={lang === "it" ? "Descrizione — Italiano" : "Description — Italiano (optional)"}><Textarea rows={4} value={g.desc_it} onChange={(e) => setG("desc_it", e.target.value)} className={inputCls} /></Fld>
          </div>
        </Section>

        </div>
        <div hidden={activePage !== "gallery"}>
        <Section title={it ? "Social e video" : "Social links and video"}><SubjectMediaFields socialLinks={g.social_links} video={g.video} onSocialChange={value=>setG("social_links",value)} onVideoChange={value=>setG("video",value)} onBusyChange={setMediaUploading} /></Section>
        <Section title={it ? "Galleria fotografica" : "Photo gallery"}><GalleryEditor originalLanguage={data.subject.original_language} farmId={id} /></Section>

        </div>
        <div hidden={activePage !== "areas"}>
        {hasPlantAreas(data.subject.activities) && <>
        <CropAreas mode="details" botanicalSelections={data.references.reference_botanical_selections} botanicalOnly={!hasCropCultivation(data.subject.activities)} originalLanguage={data.subject.original_language} generalPractices={data.practices} value={data.fields.filter(row=>hasCropCultivation(data.subject.activities) || row.reference_species_id)} onChange={rows => upd("fields", [...data.fields.filter(row=>!hasCropCultivation(data.subject.activities) && !row.reference_species_id),...rows])} referenceIds={hasCropCultivation(data.subject.activities) ? data.references.reference_crop_ids : []} legacyCrops={hasCropCultivation(data.subject.activities) ? data.crops.map(c => c.name) : []} />

        {/* Preserved legacy crop descriptions; the reference picker is the primary selection. */}
        {hasCropCultivation(data.subject.activities) && !!data.crops.length && <details><summary className="text-sm underline">{it ? "Dati colturali precedenti e varietà dichiarate" : "Previous crop descriptions and declared varieties"}</summary>
        <Section title={t("auth.secCrops")}>
          <ArrayEditor
            items={data.crops} testid="crops"
            onAdd={() => upd("crops", [...data.crops, { name: "", scientific_name: "", varieties: "", image: "" }])}
            onRemove={(i) => upd("crops", data.crops.filter((_, x) => x !== i))}
            render={(it, i, set) => (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Fld label={t("auth.crop")}><Input value={it.name} onChange={(e) => set({ ...it, name: e.target.value })} className={inputCls} /></Fld>
                <Fld label={t("auth.scientificName")}><Input value={it.scientific_name} onChange={(e) => set({ ...it, scientific_name: e.target.value })} className={inputCls} /></Fld>
                <Fld label={t("auth.varieties")}><Input value={it.varieties} onChange={(e) => set({ ...it, varieties: e.target.value })} className={inputCls} /></Fld>
              </div>
            )}
            addLabel={t("auth.addCrop")} removeLabel={t("auth.remove")}
            updateSection={(items) => upd("crops", items)}
          />
        </Section>

        </details>}
        {hasPlantManagement(data.subject.activities) && <Section title={t("auth.secPractices")}>
          <p className="text-sm mb-4">{it ? "Le pratiche sono associate alle colture o specie e alle rispettive aree. Le scelte già compilate sono riportate qui e restano distinte per appezzamento." : "Practices belong to crops or species and their respective areas. Previously entered choices appear here and remain separate for each plot."}</p>
          <CropAreas mode="practices" botanicalSelections={data.references.reference_botanical_selections} botanicalOnly={!hasCropCultivation(data.subject.activities)} originalLanguage={data.subject.original_language} generalPractices={data.practices} value={data.fields.filter(row=>hasCropCultivation(data.subject.activities) || row.reference_species_id)} onChange={rows => upd("fields", [...data.fields.filter(row=>!hasCropCultivation(data.subject.activities) && !row.reference_species_id),...rows])} referenceIds={hasCropCultivation(data.subject.activities) ? data.references.reference_crop_ids : []} legacyCrops={hasCropCultivation(data.subject.activities) ? data.crops.map(c => c.name) : []} />
          <details className="mt-5 border rounded p-3"><summary className="cursor-pointer font-semibold">{it ? "Pratiche generali del soggetto (facoltative)" : "Subject-wide general practices (optional)"}</summary><p className="text-sm mt-2">{it ? "Valori comuni da ereditare nelle aree. Non sono un riepilogo delle pratiche specifiche: ogni area può mantenere valori personalizzati." : "Common defaults inherited by areas. These are not a summary of area-specific practices: each area may retain its own values."}</p><PracticesFields originalLanguage={data.subject.original_language} value={data.practices} onChange={v => upd("practices", v)} /></details>
        </Section>}

        </>}
        </div>
        <div hidden={activePage !== "products"}>
        {/* Brands & products */}
        <Section title={t("auth.secBrands")}>
          <p className="text-sm mb-4">{lang === 'it' ? 'Free comprende 1 marchio e 1 prodotto o produzione. Pro permette più marchi, fino a 100 prodotti complessivi. I marchi e prodotti già presenti vengono conservati.' : 'Free includes 1 brand and 1 product or production entry. Pro allows multiple brands, with up to 100 products in total. Existing brands and products are preserved.'}</p>
          <ArrayEditor
            items={data.brands} testid="brands"
            canAdd={!!subscription && data.brands.length < (subscription.entitlements?.max_brands ?? 1)}
            onAdd={() => upd("brands", [...data.brands, { name: "", shop_url: "", products: [] }])}
            onRemove={(i) => upd("brands", data.brands.filter((_, x) => x !== i))}
            render={(b, bi, set) => (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Fld label={t("auth.brandName")}><Input value={b.name} onChange={(e) => set({ ...b, name: e.target.value })} className={inputCls} /></Fld>
                  <Fld label={it ? "Link al prodotto o allo shop" : "Link to the product or shop"}><Input value={b.shop_url} onChange={(e) => set({ ...b, shop_url: e.target.value })} className={inputCls} /></Fld>
                </div>
                <div className="pl-3 border-l-2 border-[#E2DACB] space-y-3">
                  {b.products.map((p, pi) => (
                    <div key={pi} className="grid grid-cols-1 sm:grid-cols-2 gap-3 relative">
                      <div className="sm:col-span-2"><BilingualField label={t("auth.productName")} value={p.name} originalLanguage={farm.original_language || "it"} multiline={false} maxLength={200} onChange={name=>{const products=[...b.products];products[pi]={...p,name};set({...b,products});}} /><BilingualField label={it ? "Descrizione del prodotto" : "Product description"} value={p.description} originalLanguage={farm.original_language || "it"} onChange={description=>{const products=[...b.products];products[pi]={...p,description};set({...b,products});}} /></div>
                      <Fld label={t("auth.externalUrl")}><Input value={p.external_url} onChange={(e) => { const prods = [...b.products]; prods[pi] = { ...p, external_url: e.target.value }; set({ ...b, products: prods }); }} className={inputCls} /></Fld>
                      <div className="sm:col-span-2"><Fld label={it ? "Foto del prodotto" : "Product photo"}><MainImagePicker value={p.image} lang={lang} variant="profile" onChange={image => { const prods = [...b.products]; prods[pi] = { ...p, image }; set({ ...b, products: prods }); }} /></Fld></div>
                      <button type="button" onClick={() => set({ ...b, products: b.products.filter((_, x) => x !== pi) })} className="text-xs text-[#B3261E] justify-self-start sm:col-span-2">{t("auth.remove")}</button>
                    </div>
                  ))}
                  <button type="button" disabled={!subscription || data.brands.reduce((total, brand) => total + brand.products.length, 0) >= (subscription.entitlements?.max_products ?? 1)} onClick={() => set({ ...b, products: [...b.products, { name: "", description: "", external_url: "", image: "" }] })}
                    className="inline-flex items-center gap-1 text-sm text-[#557448] disabled:opacity-50"><Plus size={14} /> {t("auth.addProduct")}</button>
                </div>
              </div>
            )}
            addLabel="+ Brand" removeLabel={t("auth.remove")}
            updateSection={(items) => upd("brands", items)}
          />
        </Section>

        </div>
        <div hidden={activePage !== "opportunities"}>
        {/* Opportunities */}
        <Section title={t("auth.secOpps")}>
          <p className="text-sm mb-4" data-testid="project-plan-limit">{lang === 'it' ? 'Le opportunità richiedono Pro. Solo Comuni ed enti pubblici possono creare un progetto gratuito. Pro include fino a 100 progetti e 100 opportunità complessive.' : 'Opportunities require Pro. Only municipalities and public bodies may create one free project. Pro includes up to 100 projects and 100 opportunities in total.'} <Link className="underline" to={`/dashboard/farms/${id}/billing`}>{lang === 'it' ? 'Piano e incassi' : 'Plan and payments'}</Link></p>
          <ArrayEditor
            items={data.opportunities} testid="opportunities" canAdd={canAddOpportunity}
            onAdd={() => upd("opportunities", [...data.opportunities, { id: crypto.randomUUID(), type: hasPro ? "VISIT" : "PROJECT", title: "", description: "", external_url: "", season: "", ...(!hasPro ? {project_details: {}} : {}) }])}
            onRemove={(i) => upd("opportunities", data.opportunities.filter((_, x) => x !== i))}
            render={(o, i, set) => (
              <fieldset disabled={lockedOpportunity(o)} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {lockedOpportunity(o) && <p className="sm:col-span-2 text-sm">{lang === "it" ? "Opportunità precedente conservata. Per modificarla occorre Pro; puoi rimuoverlo dalla scheda." : "Existing opportunity preserved. Pro is required to edit it; you can remove it from the profile."}</p>}
                <Fld label={t("auth.oppType")}>
                  <Select value={o.type} onValueChange={(v) => set({ ...o, type: v, payment_offer: null })}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[1100]">{OPP_TYPES.filter(x => (hasPro || x === "PROJECT") && (x !== "PROJECT" || o.type === "PROJECT" || data.opportunities.filter(item => item.type === "PROJECT").length < projectLimit)).map((x) => <SelectItem key={x} value={x}>{t(`opp.${x}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </Fld>
                <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">{["it", "en"].map(language => <React.Fragment key={language}>
                  <Fld label={`${it ? "Titolo" : "Title"} — ${language === "it" ? "Italiano" : "English"}`}><Input maxLength={200} value={opportunityVersions(o.title)[language] || ""} onChange={e => set({...o,title:{...opportunityVersions(o.title),[language]:e.target.value}})} className={inputCls} /></Fld>
                  <Fld label={`${it ? "Descrizione" : "Description"} — ${language === "it" ? "Italiano" : "English"}`}><Textarea rows={4} maxLength={10000} value={opportunityVersions(o.description)[language] || ""} onChange={e => set({...o,description:{...opportunityVersions(o.description),[language]:e.target.value}})} className={inputCls} /></Fld>
                </React.Fragment>)}</div>
                <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">{['it','en'].map(code=><Fld key={code} label={`${it ? 'Sito esterno' : 'External website'} — ${code==='it'?'Italiano':'English'}`}><Input type="url" maxLength={2048} value={o.external_urls?.[code] ?? (code===(farm.original_language || 'it') ? o.external_url : '')} onChange={e=>set({...o,external_urls:{...(o.external_urls || {}),[code]:e.target.value},...(code===(farm.original_language || 'it')?{external_url:e.target.value}:{})})} placeholder="https://…" className={inputCls}/></Fld>)}<p className="sm:col-span-2 text-sm text-[#6E6258]">{it?'Se inserisci un solo link sarà usato per entrambe le lingue.':'If you provide only one link, it will be used in both languages.'}</p></div>
                <div className="sm:col-span-2"><BilingualField label={it ? "Periodo / stagionalità" : "Period / seasonality"} value={o.season} originalLanguage={farm.original_language || "it"} onChange={season => set({...o,season})} multiline={false} maxLength={500} testId="opportunity-season" /></div>
                {o.type === "PROJECT" ? <div className="sm:col-span-2"><ProjectFields value={o} onChange={set} opportunities={data.opportunities} showIdentity={false} showDescription={false} originalLanguage={farm.original_language || "it"} /></div>
                  : <div className="sm:col-span-2"><div className="mt-4"><OpportunityMediaFields value={o.media || {}} onChange={media => set({...o,media})} /></div></div>}
                <div className="sm:col-span-2"><PaymentOfferFields opportunity={o} onChange={set} originalLanguage={farm.original_language || "it"} /></div>
              </fieldset>
            )}
            addLabel={t("auth.addOpportunity")} removeLabel={t("auth.remove")}
            updateSection={(items) => upd("opportunities", items)}
          />
        </Section>

        </div>
        <div className="flex justify-between items-center gap-3 mb-6">
          <Button variant="outline" disabled={pageIndex === 0} onClick={() => changePage(pages[pageIndex - 1][0])}>{it ? "Precedente" : "Previous"}</Button>
          <span className="text-sm">{pageIndex + 1} / {pages.length}</span>
          <Button variant="outline" disabled={pageIndex === pages.length - 1} onClick={() => changePage(pages[pageIndex + 1][0])}>{it ? "Successiva" : "Next"}</Button>
        </div>
        {err && <p className="text-sm text-[#B3261E] mb-3">{err}</p>}
        <div className="sticky bottom-4 z-10 flex flex-wrap justify-between items-center gap-3 rounded-xl bg-[#FFFDF8]/95 border border-[#E2DACB] p-3 shadow-sm">
          <Button onClick={openDelete} disabled={busy} variant="outline" data-testid="delete-farm"
            className="h-12 rounded-full border-[#B3261E] text-[#B3261E] hover:bg-[#B3261E]/10 px-5 gap-2">
            <Trash2 size={16} /> {it ? "Archivia profilo" : "Archive profile"}
          </Button>
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 ml-auto min-w-0 max-w-full" data-testid="save-feedback">
        {busy && <p role="status" aria-live="polite" className="flex items-center gap-2 rounded-lg bg-[#FFFDF8] border border-[#E2DACB] p-2 text-sm max-w-xs"><Loader2 size={18} className="animate-spin shrink-0" />{it ? "Salvataggio in corso…" : "Saving…"} {saveSeconds} s{saveSeconds >= 15 && (it ? " — Attendi la conferma, senza chiudere la pagina." : " — Please wait for confirmation and keep this page open.")}</p>}
          <Button onClick={save} disabled={busy || mediaUploading || dirty.size === 0} data-testid="save-farm"
            className="h-12 rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] px-8 shadow-lg gap-2">
            <Save size={16} /> {busy ? t("auth.saving") : t("auth.save")}
          </Button>
          </div>
        </div>
        {delPreview && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="delete-dialog">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h3 className="font-serif text-xl text-[#B3261E]">{it ? "Archiviare questo profilo?" : "Archive this profile?"}</h3>
              <p className="text-sm text-[#5C4636] mt-2">{it ? "L'archiviazione è recuperabile ed è distinta dall'eliminazione dell'account. Il profilo sparirà da pagine pubbliche, mappa e ricerca." : "Archival is recoverable and distinct from account deletion. The profile will disappear from public pages, map and search."}</p>
              <p className="text-sm text-[#5C4636] mt-3">{it ? "Archiviare la scheda non disdice un eventuale abbonamento Stripe. Per interrompere il rinnovo, gestisci prima l’abbonamento da Piano e incassi. Per richiedere la cancellazione dei dati personali, scrivi al contatto indicato nella Privacy." : "Archiving the profile does not cancel a Stripe subscription. To stop renewal, first manage your subscription from Plan and payments. To request erasure of personal data, contact the address in the Privacy notice."}</p>
              <ul className="text-sm text-[#2A2521] mt-3 list-disc pl-5 space-y-1">
                <li>{it ? "Aree" : "Areas"}: {delPreview.fields}</li>
                <li>{it ? "Prodotti" : "Products"}: {delPreview.products}</li>
                <li>{it ? "Opportunità" : "Opportunities"}: {delPreview.opportunities} ({it ? "progetti" : "projects"}: {delPreview.projects})</li>
                <li>{it ? "Immagini galleria" : "Gallery images"}: {delPreview.gallery}</li>
              </ul>
              <div className="flex justify-end gap-3 mt-5">
                <Button variant="outline" onClick={() => setDelPreview(null)} className="rounded-full">{it ? "Annulla" : "Cancel"}</Button>
                <Button onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete"
                  className="rounded-full bg-[#B3261E] hover:bg-[#8f1e18] text-white">{deleting ? (it ? "Archivio…" : "Archiving…") : (it ? "Conferma archiviazione" : "Confirm archival")}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-[#FFFDF8] border border-[#E2DACB] rounded-md p-6 mb-5">
    <h2 className="font-serif text-xl font-semibold text-[#2A2521] mb-4">{title}</h2>
    {children}
  </div>
);

const ArrayEditor = ({ items, render, onAdd, onRemove, addLabel, removeLabel, updateSection, testid, canAdd = true }) => (
  <div className="space-y-4" data-testid={`editor-${testid}`}>
    {testid === "opportunities" && <button type="button" onClick={onAdd} disabled={!canAdd} className="inline-flex items-center gap-1.5 rounded-full border border-[#557448] px-4 py-2 text-sm text-[#557448] disabled:opacity-40 disabled:cursor-not-allowed"><Plus size={15} /> {addLabel}</button>}
    <RecordPages items={items} label={(row,index)=>row.title || row.name || String(index+1)}>{(it, i) => (
      <div key={i} className="border border-[#EDE7D7] rounded-md p-4 relative bg-[#FDFBF5]">
        {render(it, i, (nv) => { const copy = [...items]; copy[i] = nv; updateSection(copy); })}
        <button type="button" onClick={() => onRemove(i)}
          className="absolute top-3 right-3 text-[#B3261E] hover:text-[#8f1e18]" title={removeLabel}>
          <Trash2 size={15} />
        </button>
      </div>
    )}</RecordPages>
    <button type="button" onClick={onAdd} disabled={!canAdd}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#557448] hover:gap-2.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
      <Plus size={15} /> {addLabel}
    </button>
  </div>
);



